import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Slider } from '../ui/slider';
import { Combobox } from '../ui/combobox';
import {
    MachineFilterOptions,
    MachineFilterState,
    NO_LOCATION,
    NO_CATEGORY,
} from './types';
import { Label } from '../ui/label';
import {
    categoryKey,
    getCategoryColorClasses,
} from '../ui-components/category/categoryColors';

interface MachineFiltersProps {
    filterOptions: MachineFilterOptions | null;
    filters: MachineFilterState;
    searchTerm: string;
    onSearchChange: (term: string) => void;
    onFilterChange: (filters: MachineFilterState) => void;
    onReset: () => void;
    hasFavorites?: boolean;
}

const filterFieldClass = 'min-w-0 max-w-56 flex-1 basis-40 space-y-1.5';
const wideFilterFieldClass = 'min-w-0 max-w-72 flex-1 basis-56 space-y-1.5';

export default function MachineFilters({
    filterOptions,
    filters,
    searchTerm,
    onSearchChange,
    onFilterChange,
    onReset,
    hasFavorites,
}: MachineFiltersProps) {
    const { t } = useTranslation();

    const handleFieldStrengthChange = (value: string) => {
        onFilterChange({
            ...filters,
            fieldStrength: value === 'all' ? undefined : [parseFloat(value)],
        });
    };

    const handleCoilTypeChange = (value: string) => {
        onFilterChange({
            ...filters,
            coilType: value ? [value] : undefined,
        });
    };

    const handleSpatialGradientChange = (value: number[]) => {
        onFilterChange({
            ...filters,
            maxSpatialGradient: value[0],
        });
    };

    const handleLocationChange = (value: string) => {
        onFilterChange({
            ...filters,
            location: value === 'all' ? undefined : value,
        });
    };

    const handleCategoryChange = (value: string) => {
        onFilterChange({
            ...filters,
            category: value === 'all' ? undefined : value,
        });
    };

    const handleFavoritesToggle = () => {
        onFilterChange({
            ...filters,
            favoritesOnly: !filters.favoritesOnly,
        });
    };

    const maxGradientValue = filterOptions?.maxSpatialGradient || 50;
    const currentGradientValue = filters.maxSpatialGradient ?? maxGradientValue;

    // Check if any filters are active
    const hasActiveFilters =
        searchTerm ||
        filters.fieldStrength?.length ||
        filters.coilType?.length ||
        filters.favoritesOnly ||
        filters.location ||
        filters.category ||
        (filters.maxSpatialGradient !== undefined &&
            filters.maxSpatialGradient < maxGradientValue);

    return (
        <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
                <Search
                    className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle-foreground"
                    aria-hidden="true"
                />
                <Input
                    placeholder={t(
                        'roomOverview.machineFilters.searchPlaceholder'
                    )}
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-9"
                    aria-label={t(
                        'roomOverview.machineFilters.searchAriaLabel'
                    )}
                />
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap items-end gap-3">
                {filterOptions?.locations.length ? (
                    <div className={filterFieldClass}>
                        <Label htmlFor="filter-location">
                            {t('roomOverview.machineFilters.location')}
                        </Label>
                        <Select
                            value={filters.location || 'all'}
                            onValueChange={handleLocationChange}
                        >
                            <SelectTrigger id="filter-location">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    {t(
                                        'roomOverview.machineFilters.allLocations'
                                    )}
                                </SelectItem>
                                {filterOptions.locations.map((location) => (
                                    <SelectItem key={location} value={location}>
                                        {location}
                                    </SelectItem>
                                ))}
                                <SelectItem value={NO_LOCATION}>
                                    {t(
                                        'roomOverview.roomOverview.sections.other'
                                    )}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                ) : null}

                {filterOptions?.categories.length ? (
                    <div className={filterFieldClass}>
                        <Label htmlFor="filter-category">
                            {t('roomOverview.machineFilters.category')}
                        </Label>
                        <Select
                            value={filters.category || 'all'}
                            onValueChange={handleCategoryChange}
                        >
                            <SelectTrigger id="filter-category">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    {t(
                                        'roomOverview.machineFilters.allCategories'
                                    )}
                                </SelectItem>
                                {filterOptions.categories.map((category) => (
                                    <SelectItem
                                        key={categoryKey(category)}
                                        value={categoryKey(category)}
                                    >
                                        <span className="flex items-center gap-2">
                                            <span
                                                className={cn(
                                                    'h-2 w-2 shrink-0 rounded-full',
                                                    getCategoryColorClasses(
                                                        category.color
                                                    ).swatch
                                                )}
                                            />
                                            {category.name}
                                        </span>
                                    </SelectItem>
                                ))}
                                <SelectItem value={NO_CATEGORY}>
                                    {t(
                                        'roomOverview.machineFilters.noCategory'
                                    )}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                ) : null}

                <div className={filterFieldClass}>
                    <Label htmlFor="filter-field-strength">
                        {t('roomOverview.machineFilters.fieldStrength')}
                    </Label>
                    <Select
                        value={filters.fieldStrength?.[0]?.toString() || 'all'}
                        onValueChange={handleFieldStrengthChange}
                    >
                        <SelectTrigger id="filter-field-strength">
                            <SelectValue
                                placeholder={t(
                                    'roomOverview.machineFilters.allFieldStrengths'
                                )}
                            />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                {t(
                                    'roomOverview.machineFilters.allFieldStrengths'
                                )}
                            </SelectItem>
                            {filterOptions?.fieldStrengths?.map((fs) => (
                                <SelectItem key={fs} value={fs.toString()}>
                                    {fs} Tesla
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className={filterFieldClass}>
                    <Label>{t('roomOverview.machineFilters.coilType')}</Label>
                    <Combobox
                        options={filterOptions?.coilTypes || []}
                        value={filters.coilType?.[0] || ''}
                        onChange={handleCoilTypeChange}
                        placeholder={t('roomOverview.machineFilters.allCoils')}
                    />
                </div>

                <div className={wideFilterFieldClass}>
                    <div className="flex items-baseline justify-between gap-2">
                        <Label htmlFor="filter-gradient" className="truncate">
                            {t(
                                'roomOverview.machineFilters.maxSpatialGradient'
                            )}
                        </Label>
                        <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-subtle-foreground">
                            {currentGradientValue} T/m
                        </span>
                    </div>
                    <div className="flex h-9 items-center">
                        <Slider
                            id="filter-gradient"
                            value={[currentGradientValue]}
                            onValueChange={handleSpatialGradientChange}
                            max={maxGradientValue}
                            min={0}
                            step={0.1}
                            aria-label={`${t('roomOverview.machineFilters.maxSpatialGradient')}: ${currentGradientValue} T/m`}
                        />
                    </div>
                </div>

                {hasFavorites && (
                    <div className="shrink-0 space-y-1.5">
                        <Label htmlFor="filter-favorites">
                            {t('roomOverview.machineFilters.favoritesOnly')}
                        </Label>
                        <div className="flex h-9 items-center">
                            <Switch
                                id="filter-favorites"
                                checked={!!filters.favoritesOnly}
                                onCheckedChange={handleFavoritesToggle}
                            />
                        </div>
                    </div>
                )}

                <div className="flex h-9 items-center ml-auto">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onReset}
                        aria-hidden={!hasActiveFilters}
                        tabIndex={hasActiveFilters ? undefined : -1}
                        className={cn(
                            'gap-1 text-gray-400 hover:text-foreground',
                            !hasActiveFilters && 'invisible'
                        )}
                    >
                        <X className="h-4 w-4" aria-hidden="true" />
                        {t('roomOverview.machineFilters.clearFilters')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
